import React, { useMemo, useState } from 'react'
import {
    Avatar,
    Box,
    Chip,
    Dialog,
    DialogContent,
    IconButton,
    Tooltip,
    Theme,
    Typography,
    useTheme,
} from '@material-ui/core'
import { ChatBubbleOutline, InfoOutlined } from '@material-ui/icons'
import { Rating } from '@material-ui/lab'

import { ReactComponent as RibbonThumbGreenIcon } from '../../assets/icons/thumbs_ribbon-32-filled-big.svg'
import { getInitials } from '../../helpers'
import {
    Bid,
    BidInviteTier,
    BidItem,
    BidStatus,
    GeneratedRFPArea,
    GeneratedRFPMixGroup,
    GeneratedRFPMixUnitType,
    GeneratedRFPUnitMix,
    RFP,
} from '../../models'
import {
    NVLVendorTier,
    NVL_TIER_LABELS,
} from '../../main/containers/national-vendor-list/nvlVendorModel'
import { typographyStyle } from '../../theme/typographyStyle'
import { RFPStatusBadge } from './RFPStatusBadge'
import { RFPVendorContractActions } from './RFPVendorContractActions'

interface Props {
    rfp: RFP
    bids: Bid[]
    setSelectedBid: (bid: Bid) => void
    openMessageDrawer: (bid: Bid) => void
    onBidUpdated?: (bid: Bid) => void
}

const INVITE_LABEL: Record<BidInviteTier, string> = {
    [BidInviteTier.CURRENT]: 'Current',
    [BidInviteTier.NEARBY]: 'Nearby',
    [BidInviteTier.WILL_TRAVEL]: 'Will travel',
}

const money = (value: number) =>
    `$${value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })}`

const formatCompleteCount = (count: number) => {
    if (count >= 1000) {
        return `${Math.floor(count / 1000)}K+`
    }
    if (count >= 100) {
        return `${Math.floor(count / 100) * 100}+`
    }
    return String(count)
}

const tierColorFor = (tier: NVLVendorTier, theme: Theme) => {
    if (tier === 'silver') {
        return theme.palette.silver.main
    }
    if (tier === 'gold') {
        return theme.palette.gold.main
    }
    if (tier === 'blue') {
        return theme.palette.blue.main
    }
    if (tier === 'dark_blue') {
        return theme.palette.darkBlue.main
    }
    return theme.palette.primary.dark
}

const bidItemsFor = (bid: Bid) =>
    (bid.service_bids || []).flatMap((serviceBid) => serviceBid.bid_items || [])

const itemPrice = (item?: BidItem | null) => {
    if (item == null || item.price == null || item.price === ('' as unknown)) {
        return null
    }
    const value = Number(item.price)
    return Number.isFinite(value) ? value : null
}

const unitPriceMap = (bid: Bid) => {
    const prices = new Map<number, number>()
    bidItemsFor(bid).forEach((item) => {
        const unitId = item.unit_config?.id
        const price = itemPrice(item)
        if (unitId != null && price != null) {
            prices.set(unitId, price)
        }
    })
    return prices
}

const areaItemsFor = (bid: Bid) =>
    bidItemsFor(bid).filter((item) => item.area_config)

const representativePrice = (
    unitTypes: GeneratedRFPMixUnitType[],
    prices: Map<number, number>,
) => {
    let weighted = 0
    let weight = 0
    let low: number | null = null
    let high: number | null = null
    let priced = 0
    unitTypes.forEach((unitType) => {
        const price = prices.get(unitType.unit_config_id)
        if (price == null) {
            return
        }
        const unitWeight =
            unitType.inventory_count && unitType.inventory_count > 0
                ? unitType.inventory_count
                : 1
        weighted += price * unitWeight
        weight += unitWeight
        priced += 1
        low = low == null ? price : Math.min(low, price)
        high = high == null ? price : Math.max(high, price)
    })
    if (!weight) {
        return { avg: null as number | null, low, high, priced }
    }
    return { avg: weighted / weight, low, high, priced }
}

const groupImpliedTotal = (
    group: GeneratedRFPMixGroup,
    prices: Map<number, number>,
) => {
    if (group.full_count == null) {
        return null
    }
    const { avg } = representativePrice(group.unit_types || [], prices)
    if (avg == null) {
        return null
    }
    return avg * group.full_count
}

const impliedTotalFor = (
    groups: GeneratedRFPMixGroup[],
    prices: Map<number, number>,
) => {
    let total = 0
    let any = false
    groups.forEach((group) => {
        const part = groupImpliedTotal(group, prices)
        if (part != null) {
            total += part
            any = true
        }
    })
    return any ? total : null
}

const groupsFromUnitMix = (
    unitMix: GeneratedRFPUnitMix[],
): GeneratedRFPMixGroup[] => {
    const byBed = new Map<number | 'unknown', GeneratedRFPMixGroup>()
    unitMix.forEach((row) => {
        const key = row.bed_count ?? 'unknown'
        const existing = byBed.get(key)
        if (!existing) {
            const bedCount = row.bed_count ?? null
            byBed.set(key, {
                bed_count: bedCount,
                bath_count: null,
                label:
                    bedCount === 0
                        ? 'Studio'
                        : bedCount == null
                        ? 'Unknown'
                        : bedCount === 1
                        ? '1 bedroom'
                        : `${bedCount} bedrooms`,
                full_count: row.full_count,
                partial_count: row.partial_count,
                source: 'historical',
                unit_types: [
                    {
                        unit_config_id: row.unit_config_id,
                        name: row.name,
                        bed_count: row.bed_count,
                        bath_count: row.bath_count,
                        min_square_footage: row.min_square_footage,
                        max_square_footage: row.max_square_footage,
                        floorplan_image: row.floorplan_image,
                    },
                ],
            })
            return
        }
        existing.unit_types.push({
            unit_config_id: row.unit_config_id,
            name: row.name,
            bed_count: row.bed_count,
            bath_count: row.bath_count,
            min_square_footage: row.min_square_footage,
            max_square_footage: row.max_square_footage,
            floorplan_image: row.floorplan_image,
        })
    })
    return Array.from(byBed.values())
}

const shortGroupLabel = (group: GeneratedRFPMixGroup) => {
    if (group.bed_count === 0) {
        return 'Studio'
    }
    if (group.bed_count == null) {
        return group.label || 'Unknown'
    }
    if (group.bed_count === 1) {
        return '1 bed'
    }
    return `${group.bed_count} bed`
}

export const GeneratedBidComparison = (props: Props) => {
    const { rfp, bids, setSelectedBid, openMessageDrawer, onBidUpdated } = props
    const theme = useTheme()
    const [detailBid, setDetailBid] = useState<Bid | null>(null)

    const mixGroups = useMemo(() => {
        const snapshotGroups = rfp.scope_snapshot?.mix_groups
        if (snapshotGroups && snapshotGroups.length > 0) {
            return snapshotGroups
        }
        return groupsFromUnitMix(rfp.scope_snapshot?.unit_mix || [])
    }, [rfp.scope_snapshot])

    const areas: GeneratedRFPArea[] = rfp.scope_snapshot?.areas || []
    const propertyNotes = (rfp.scope_snapshot?.notes || '').trim()

    const sortedBids = useMemo(() => {
        const withTotals = bids.map((bid) => {
            const prices = unitPriceMap(bid)
            const submitted = prices.size > 0
            const implied = submitted
                ? impliedTotalFor(mixGroups, prices)
                : null
            return { bid, prices, submitted, implied }
        })
        return withTotals.sort((a, b) => {
            if (a.submitted !== b.submitted) {
                return a.submitted ? -1 : 1
            }
            if (a.implied == null && b.implied == null) {
                return a.bid.vendor.name.localeCompare(b.bid.vendor.name)
            }
            if (a.implied == null) {
                return 1
            }
            if (b.implied == null) {
                return -1
            }
            return a.implied - b.implied
        })
    }, [bids, mixGroups])

    const headerStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.caption),
        fontWeight: 700,
        color: theme.palette.darkGreen.main,
        textAlign: 'left',
        padding: '6px 8px',
        whiteSpace: 'nowrap',
    }
    const cell: React.CSSProperties = {
        ...typographyStyle(theme.typography.body2),
        padding: '8px',
        verticalAlign: 'middle',
    }

    const renderTierChip = (vendor: Bid['vendor']) => {
        const tier = (vendor.tier ?? 'silver') as NVLVendorTier
        const tierColor = tierColorFor(tier, theme)
        return (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: `${tierColor}22`,
                    border: `1px solid ${tierColor}`,
                    borderRadius: 16,
                    padding: '1px 8px',
                    fontSize: 11,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                }}
            >
                {NVL_TIER_LABELS[tier]}
            </span>
        )
    }

    return (
        <>
            <Box
                style={{
                    overflow: 'auto',
                    margin: theme.spacing(1),
                    border: `1px solid ${theme.palette.grey[200]}`,
                    borderRadius: 4,
                }}
            >
                <table
                    style={{
                        width: '100%',
                        minWidth: 960,
                        borderCollapse: 'collapse',
                    }}
                >
                    <thead>
                        <tr>
                            <th style={{ ...headerStyle, minWidth: 180 }}>
                                Vendor
                            </th>
                            <th style={headerStyle}>Status</th>
                            <th style={headerStyle}>Rating</th>
                            <th style={headerStyle}>Go backs</th>
                            <th style={headerStyle}>Completed</th>
                            <th style={headerStyle}>Miles</th>
                            {mixGroups.map((group) => (
                                <th
                                    key={`group-${group.bed_count}`}
                                    style={headerStyle}
                                >
                                    {shortGroupLabel(group)}
                                </th>
                            ))}
                            <th style={headerStyle}>
                                <Box
                                    display="inline-flex"
                                    alignItems="center"
                                    style={{ gap: 4 }}
                                >
                                    Implied total
                                    <Tooltip title="Implied total = expected full units × inventory-weighted average unit price per bedroom group. Estimate only.">
                                        <InfoOutlined
                                            style={{
                                                fontSize: 14,
                                                color: theme.palette.grey[600],
                                                verticalAlign: 'middle',
                                            }}
                                        />
                                    </Tooltip>
                                </Box>
                            </th>
                            <th style={headerStyle}>Bid status</th>
                            <th style={{ ...headerStyle, width: 140 }} />
                            <th style={{ ...headerStyle, width: 48 }} />
                        </tr>
                    </thead>
                    <tbody>
                        {sortedBids.map(
                            ({ bid, prices, submitted, implied }) => {
                                const addressCity =
                                    bid.vendor.primary_address?.city
                                const ratingValue = Number(
                                    bid.vendor.rating ?? 0,
                                )
                                const hasRating = ratingValue > 0
                                const completeCount =
                                    bid.vendor.complete_count ?? 0
                                return (
                                    <tr
                                        key={bid.id}
                                        onClick={() => {
                                            setSelectedBid(bid)
                                            setDetailBid(bid)
                                        }}
                                        style={{
                                            cursor: 'pointer',
                                            borderTop: `1px solid ${theme.palette.grey[200]}`,
                                        }}
                                    >
                                        <td style={cell}>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                style={{ gap: 8 }}
                                            >
                                                {bid.vendor.profile_picture ? (
                                                    <img
                                                        src={
                                                            bid.vendor
                                                                .profile_picture
                                                        }
                                                        alt=""
                                                        style={{
                                                            width: 36,
                                                            height: 36,
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                ) : (
                                                    <Avatar
                                                        variant="square"
                                                        style={{
                                                            backgroundColor:
                                                                theme.palette
                                                                    .primary
                                                                    .dark,
                                                            width: 36,
                                                            height: 36,
                                                        }}
                                                    >
                                                        {getInitials(
                                                            bid.vendor.name,
                                                        )}
                                                    </Avatar>
                                                )}
                                                <Box>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        style={{ gap: 4 }}
                                                    >
                                                        <Typography
                                                            style={{
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {bid.vendor.name}
                                                        </Typography>
                                                        {bid.vendor
                                                            .is_trained ? (
                                                            <Tooltip title="EZ Trained">
                                                                <span
                                                                    style={{
                                                                        display:
                                                                            'flex',
                                                                    }}
                                                                >
                                                                    <RibbonThumbGreenIcon
                                                                        style={{
                                                                            width: 16,
                                                                            height: 16,
                                                                        }}
                                                                    />
                                                                </span>
                                                            </Tooltip>
                                                        ) : null}
                                                    </Box>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        style={{
                                                            gap: 4,
                                                            marginTop: 2,
                                                        }}
                                                    >
                                                        {bid.invite_tier && (
                                                            <Chip
                                                                size="small"
                                                                label={
                                                                    INVITE_LABEL[
                                                                        bid
                                                                            .invite_tier
                                                                    ]
                                                                }
                                                            />
                                                        )}
                                                        {addressCity && (
                                                            <Typography
                                                                variant="caption"
                                                                color="textSecondary"
                                                            >
                                                                {addressCity}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </td>
                                        <td style={cell}>
                                            {renderTierChip(bid.vendor)}
                                        </td>
                                        <td style={cell}>
                                            {hasRating ? (
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    style={{ gap: 4 }}
                                                >
                                                    <Rating
                                                        name={`rfp-bid-rating-${bid.id}`}
                                                        value={ratingValue}
                                                        precision={0.5}
                                                        readOnly
                                                        size="small"
                                                    />
                                                    <span
                                                        style={{
                                                            fontSize: 11,
                                                            color:
                                                                theme.palette
                                                                    .grey[600],
                                                        }}
                                                    >
                                                        {ratingValue.toFixed(1)}
                                                    </span>
                                                </Box>
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                        <td style={cell}>
                                            {bid.vendor.go_back_rate != null
                                                ? `${bid.vendor.go_back_rate.toFixed(
                                                      1,
                                                  )}%`
                                                : '—'}
                                        </td>
                                        <td style={cell}>
                                            {formatCompleteCount(completeCount)}
                                        </td>
                                        <td style={cell}>
                                            {bid.distance_miles != null
                                                ? `${bid.distance_miles} mi`
                                                : '—'}
                                        </td>
                                        {mixGroups.map((group) => {
                                            const { avg } = representativePrice(
                                                group.unit_types || [],
                                                prices,
                                            )
                                            return (
                                                <td
                                                    key={`${bid.id}-${group.bed_count}`}
                                                    style={cell}
                                                >
                                                    {!submitted || avg == null
                                                        ? '—'
                                                        : money(avg)}
                                                </td>
                                            )
                                        })}
                                        <td
                                            style={{
                                                ...cell,
                                                fontWeight: 700,
                                            }}
                                        >
                                            {implied != null
                                                ? money(implied)
                                                : '—'}
                                        </td>
                                        <td style={cell}>
                                            <RFPStatusBadge
                                                status={bid.status}
                                                theme={theme}
                                            />
                                        </td>
                                        <td style={cell}>
                                            <RFPVendorContractActions
                                                bid={bid}
                                                rfp={rfp}
                                                onBidUpdated={(next) => {
                                                    onBidUpdated?.(next)
                                                    setDetailBid((current) =>
                                                        current?.id === next.id
                                                            ? {
                                                                  ...current,
                                                                  ...next,
                                                              }
                                                            : current,
                                                    )
                                                }}
                                            />
                                        </td>
                                        <td style={cell}>
                                            <IconButton
                                                onClick={(event) => {
                                                    event.stopPropagation()
                                                    openMessageDrawer(bid)
                                                }}
                                                aria-label="Open conversation"
                                            >
                                                <ChatBubbleOutline
                                                    color={
                                                        (
                                                            bid.notes || ''
                                                        ).trim() ||
                                                        (
                                                            rfp.scope_snapshot
                                                                ?.notes || ''
                                                        ).trim() ||
                                                        (bid.message_count ||
                                                            0) > 0
                                                            ? 'primary'
                                                            : 'inherit'
                                                    }
                                                />
                                            </IconButton>
                                        </td>
                                    </tr>
                                )
                            },
                        )}
                    </tbody>
                </table>
            </Box>

            <Dialog
                open={Boolean(detailBid)}
                onClose={() => setDetailBid(null)}
                fullWidth
                maxWidth="md"
            >
                <DialogContent>
                    {detailBid && (
                        <BidDetailBody
                            bid={detailBid}
                            rfp={rfp}
                            mixGroups={mixGroups}
                            areas={areas}
                            propertyNotes={propertyNotes}
                            prices={unitPriceMap(detailBid)}
                            implied={impliedTotalFor(
                                mixGroups,
                                unitPriceMap(detailBid),
                            )}
                            renderTierChip={renderTierChip}
                            onBidUpdated={(next) => {
                                onBidUpdated?.(next)
                                setDetailBid({ ...detailBid, ...next })
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    )
}

const BidDetailBody = (props: {
    bid: Bid
    rfp: RFP
    mixGroups: GeneratedRFPMixGroup[]
    areas: GeneratedRFPArea[]
    propertyNotes: string
    prices: Map<number, number>
    implied: number | null
    renderTierChip: (vendor: Bid['vendor']) => React.ReactNode
    onBidUpdated?: (bid: Bid) => void
}) => {
    const {
        bid,
        rfp,
        mixGroups,
        areas,
        propertyNotes,
        prices,
        implied,
        renderTierChip,
        onBidUpdated,
    } = props
    const theme = useTheme()
    const ratingValue = Number(bid.vendor.rating ?? 0)
    const areaItems = areaItemsFor(bid)
    const areaPrice = (areaId: number) => {
        const item = areaItems.find((row) => row.area_config?.id === areaId)
        return itemPrice(item)
    }

    return (
        <>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                style={{ gap: 8 }}
            >
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <Typography variant="h6" style={{ fontWeight: 700 }}>
                        {bid.vendor.name}
                    </Typography>
                    {bid.vendor.is_trained ? (
                        <Tooltip title="EZ Trained">
                            <span style={{ display: 'flex' }}>
                                <RibbonThumbGreenIcon
                                    style={{ width: 18, height: 18 }}
                                />
                            </span>
                        </Tooltip>
                    ) : null}
                    {renderTierChip(bid.vendor)}
                </Box>
                <RFPVendorContractActions
                    bid={bid}
                    rfp={rfp}
                    onBidUpdated={(next) => onBidUpdated?.(next)}
                />
            </Box>
            <Box
                display="flex"
                flexWrap="wrap"
                style={{ gap: 16, marginTop: 8, marginBottom: 12 }}
            >
                <Typography variant="body2" color="textSecondary">
                    Rating: {ratingValue > 0 ? ratingValue.toFixed(1) : '—'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Go backs:{' '}
                    {bid.vendor.go_back_rate != null
                        ? `${bid.vendor.go_back_rate.toFixed(1)}%`
                        : '—'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Completed:{' '}
                    {formatCompleteCount(bid.vendor.complete_count ?? 0)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {bid.distance_miles != null
                        ? `${bid.distance_miles} mi`
                        : 'Distance —'}
                </Typography>
                {bid.invite_tier && (
                    <Typography variant="body2" color="textSecondary">
                        {INVITE_LABEL[bid.invite_tier]}
                    </Typography>
                )}
                {implied != null && (
                    <Typography variant="body2" style={{ fontWeight: 700 }}>
                        Implied total {money(implied)}
                    </Typography>
                )}
            </Box>
            {propertyNotes ? (
                <Typography variant="body2" style={{ marginBottom: 8 }}>
                    {propertyNotes}
                </Typography>
            ) : null}
            <Typography variant="body2" color="textSecondary">
                {bid.notes || 'No vendor notes'}
            </Typography>
            {bid.has_vendor_coi && (
                <Typography variant="body2" style={{ marginTop: 8 }}>
                    Vendor COI uploaded
                </Typography>
            )}

            {mixGroups.map((group) => (
                <Box
                    key={`detail-${group.bed_count}`}
                    style={{ marginTop: 16 }}
                >
                    <Typography style={{ fontWeight: 700 }}>
                        {group.label}
                        {group.full_count != null
                            ? ` · ${group.full_count} expected full`
                            : ''}
                    </Typography>
                    {(group.unit_types || []).map((unitType) => {
                        const price = prices.get(unitType.unit_config_id)
                        return (
                            <Box
                                key={unitType.unit_config_id}
                                display="flex"
                                justifyContent="space-between"
                                style={{
                                    padding: '4px 0',
                                    borderBottom: `1px solid ${theme.palette.grey[200]}`,
                                }}
                            >
                                <Typography variant="body2">
                                    {unitType.name}
                                </Typography>
                                <Typography variant="body2">
                                    {price != null ? money(price) : '—'}
                                </Typography>
                            </Box>
                        )
                    })}
                </Box>
            ))}

            {areas.length > 0 && (
                <Box style={{ marginTop: 16 }}>
                    <Typography style={{ fontWeight: 700 }}>Areas</Typography>
                    {areas.map((area) => {
                        const price = areaPrice(area.area_config_id)
                        return (
                            <Box
                                key={area.area_config_id}
                                display="flex"
                                justifyContent="space-between"
                                style={{
                                    padding: '4px 0',
                                    borderBottom: `1px solid ${theme.palette.grey[200]}`,
                                }}
                            >
                                <Typography variant="body2">
                                    {area.name}
                                </Typography>
                                <Typography variant="body2">
                                    {price != null ? money(price) : '—'}
                                </Typography>
                            </Box>
                        )
                    })}
                </Box>
            )}
        </>
    )
}
